import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { HostAppShell } from '../components/HostAppShell';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  User, Mail, Phone, MapPin, Camera, Shield, Key, Clock,
  CheckCircle, AlertCircle, Loader2, Save, ExternalLink, Wallet
} from 'lucide-react';
import { toast } from 'sonner';
import { usePaymentMode } from '../contexts/PaymentModeContext';

/**
 * HostAccountPage - Unified account management for hosts
 * 
 * Sections:
 * - Profile (photo, name, location, bio)
 * - Contact (email, phone)
 * - Security (password, login methods)
 * - Preferences (placeholder for future)
 */

const HostAccountPage = () => {
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // P0 FIX: Extended form state to include all required contact fields for listing creation
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    location: '',
    bio: '',
    // Required contact/address fields for listing creation
    address_line: '',
    city: '',
    state: '',
    postcode: '',
    country: 'Australia',
    // Operating windows for pickup/return
    pickup_window_start: '08:00',
    pickup_window_end: '18:00',
    return_window_start: '08:00',
    return_window_end: '18:00'
  });
  
  const australianStates = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
  
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);

  // PHASE B7: "How you get paid" — advisory payout method
  const { isHostDirect } = usePaymentMode();
  const [payoutMethod, setPayoutMethod] = useState({
    type: '',
    payid_handle: '',
    bsb: '',
    account_number: '',
    account_name: '',
    stripe_url: '',
    notes: ''
  });
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [payoutSaving, setPayoutSaving] = useState(false);
  const [payoutLastSavedAt, setPayoutLastSavedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPayoutLoading(true);
      try {
        const res = await api.get('/users/host-payout-method');
        if (cancelled) return;
        const pm = res?.data?.payout_method || {};
        setPayoutMethod({
          type: pm.type || '',
          payid_handle: pm.payid_handle || '',
          bsb: pm.bsb || '',
          account_number: pm.account_number || '',
          account_name: pm.account_name || '',
          stripe_url: pm.stripe_url || '',
          notes: pm.notes || ''
        });
        setPayoutLastSavedAt(pm.updated_at || null);
      } catch (_) {
        /* silent — default empty */
      } finally {
        if (!cancelled) setPayoutLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  const handlePayoutChange = (field, value) => {
    setPayoutMethod(prev => ({ ...prev, [field]: value }));
  };

  const handleSavePayoutMethod = async () => {
    setPayoutSaving(true);
    try {
      const body = { ...payoutMethod, type: payoutMethod.type || null };
      const res = await api.put('/users/host-payout-method', body);
      const saved = res?.data?.payout_method;
      if (saved) setPayoutLastSavedAt(saved.updated_at);
      toast.success('Payout details saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save payout details');
    } finally {
      setPayoutSaving(false);
    }
  };

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      const hostProfile = user.host_profile || {};
      const operatingWindows = hostProfile.operating_windows || {};
      
      setFormData({
        full_name: user.full_name || '',
        phone: user.phone || '',
        location: user.location || '',
        bio: user.bio || '',
        // P0 FIX: Load existing contact/address fields
        address_line: user.address_line || '',
        city: user.city || '',
        state: user.state || '',
        postcode: user.postcode || '',
        country: user.country || 'Australia',
        // Operating windows
        pickup_window_start: operatingWindows.pickup_window_start || '08:00',
        pickup_window_end: operatingWindows.pickup_window_end || '18:00',
        return_window_start: operatingWindows.return_window_start || '08:00',
        return_window_end: operatingWindows.return_window_end || '18:00'
      });
      setProfilePhoto(user.profile_photo || null);
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      // Save main profile
      await api.put('/users/profile', formData);
      
      // Save operating windows separately
      await api.put('/users/host-operating-windows', {
        pickup_window_start: formData.pickup_window_start,
        pickup_window_end: formData.pickup_window_end,
        return_window_start: formData.return_window_start,
        return_window_end: formData.return_window_end
      });
      
      await refreshUser();
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setPhotoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/users/profile-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setProfilePhoto(res.data.photo_url);
      await refreshUser();
      toast.success('Profile photo updated');
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
    } finally {
      setPhotoUploading(false);
    }
  };

  const getVerificationBadge = () => {
    const status = user?.verification_status;
    if (status === 'verified') {
      return (
        <Badge className="bg-green-100 text-green-700 border-green-200 gap-1">
          <CheckCircle className="w-3 h-3" />
          Verified
        </Badge>
      );
    }
    if (status === 'pending') {
      return (
        <Badge className="bg-amber-100 text-amber-700 border-amber-200 gap-1">
          <Loader2 className="w-3 h-3 animate-spin" />
          Pending
        </Badge>
      );
    }
    return (
      <Badge className="bg-slate-100 text-slate-700 border-slate-200 gap-1">
        <AlertCircle className="w-3 h-3" />
        Unverified
      </Badge>
    );
  };

  return (
    <HostAppShell title="Account" activeId="account">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-heading font-bold text-slate-900">Your Account</h2>
            <p className="text-slate-500 mt-1">Manage your profile and account settings</p>
          </div>
          {getVerificationBadge()}
        </div>

        {/* Profile Section */}
        <Card className="border-slate-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-[#004B8D]" />
              Profile
            </CardTitle>
            <CardDescription>Your public profile information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Profile Photo */}
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden">
                  {profilePhoto ? (
                    <img 
                      src={profilePhoto} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-8 h-8 text-slate-400" />
                  )}
                </div>
                <label 
                  htmlFor="photo-upload" 
                  className="absolute -bottom-1 -right-1 w-8 h-8 bg-[#004B8D] rounded-full flex items-center justify-center cursor-pointer hover:bg-[#003a6d] transition-colors"
                >
                  {photoUploading ? (
                    <Loader2 className="w-4 h-4 text-white animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4 text-white" />
                  )}
                </label>
                <input 
                  id="photo-upload" 
                  type="file" 
                  accept="image/*" 
                  className="hidden" 
                  onChange={handlePhotoUpload}
                  disabled={photoUploading}
                />
              </div>
              <div>
                <p className="font-medium text-slate-900">{user?.full_name || 'Your Name'}</p>
                <p className="text-sm text-slate-500">Host since {new Date(user?.created_at || Date.now()).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}</p>
              </div>
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name</Label>
              <Input 
                id="full_name" 
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                placeholder="Your full name"
              />
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  id="location" 
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="City, State"
                  className="pl-10"
                />
              </div>
            </div>

            {/* Bio */}
            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea 
                id="bio" 
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                placeholder="Tell renters a bit about yourself..."
                rows={3}
              />
              <p className="text-xs text-slate-500">This will be shown on your public host profile</p>
            </div>
          </CardContent>
        </Card>

        {/* Contact Section */}
        <Card className="border-slate-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#004B8D]" />
              Contact Information
            </CardTitle>
            <CardDescription>How renters can reach you</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Email - Read Only */}
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  id="email" 
                  value={user?.email || ''}
                  disabled
                  className="pl-10 bg-slate-50"
                />
              </div>
              <p className="text-xs text-slate-500">Email cannot be changed. Contact support if needed.</p>
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  id="phone" 
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="0400 000 000"
                  className="pl-10"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* PHASE B7: How you get paid (advisory payout method) */}
        <Card className="border-slate-100" data-testid="payout-method-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Wallet className="w-5 h-5 text-[#004B8D]" />
              How you get paid
            </CardTitle>
            <CardDescription>
              {isHostDirect
                ? 'Share these details with renters so they can pay you directly. HireHub does not process or verify these details.'
                : 'Your preferred off-platform payout details. Advisory only — HireHub does not process these.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {payoutLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading saved details…
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Preferred payment method</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="payout-method-type-group">
                    {[
                      { value: 'payid', label: 'PayID' },
                      { value: 'bank', label: 'Bank transfer' },
                      { value: 'stripe', label: 'External Stripe' },
                      { value: 'other', label: 'Other' }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handlePayoutChange('type', opt.value)}
                        className={`border rounded-md px-3 py-2 text-sm transition-colors ${
                          payoutMethod.type === opt.value
                            ? 'border-[#004B8D] bg-blue-50 text-[#004B8D] font-medium'
                            : 'border-slate-300 bg-white text-slate-700 hover:border-[#004B8D]/60'
                        }`}
                        data-testid={`payout-method-type-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {payoutMethod.type === 'payid' && (
                  <div className="space-y-2">
                    <Label htmlFor="payid_handle">PayID (email or mobile)</Label>
                    <Input
                      id="payid_handle"
                      value={payoutMethod.payid_handle}
                      onChange={e => handlePayoutChange('payid_handle', e.target.value)}
                      placeholder="you@example.com or 0400 000 000"
                      data-testid="payout-payid-handle"
                    />
                  </div>
                )}

                {payoutMethod.type === 'bank' && (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="account_name">Account name</Label>
                      <Input
                        id="account_name"
                        value={payoutMethod.account_name}
                        onChange={e => handlePayoutChange('account_name', e.target.value)}
                        placeholder="Your name as it appears on the account"
                        data-testid="payout-account-name"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="bsb">BSB</Label>
                        <Input
                          id="bsb"
                          value={payoutMethod.bsb}
                          onChange={e => handlePayoutChange('bsb', e.target.value)}
                          placeholder="000-000"
                          data-testid="payout-bsb"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="account_number">Account number</Label>
                        <Input
                          id="account_number"
                          value={payoutMethod.account_number}
                          onChange={e => handlePayoutChange('account_number', e.target.value)}
                          placeholder="123456789"
                          data-testid="payout-account-number"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {payoutMethod.type === 'stripe' && (
                  <div className="space-y-2">
                    <Label htmlFor="stripe_url">Stripe payment link</Label>
                    <Input
                      id="stripe_url"
                      value={payoutMethod.stripe_url}
                      onChange={e => handlePayoutChange('stripe_url', e.target.value)}
                      placeholder="https://checkout.stripe.com/…"
                      data-testid="payout-stripe-url"
                    />
                    <p className="text-xs text-slate-500">
                      HireHub does not process payments — this link is shared with renters only as a convenience.
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="payout_notes">Instructions (optional)</Label>
                  <Textarea
                    id="payout_notes"
                    value={payoutMethod.notes}
                    onChange={e => handlePayoutChange('notes', e.target.value)}
                    rows={3}
                    maxLength={500}
                    placeholder="e.g. Please include your booking ID in the payment reference."
                    data-testid="payout-notes"
                  />
                </div>

                <div className="flex items-center justify-between pt-2">
                  <p className="text-xs text-slate-500">
                    {payoutLastSavedAt
                      ? `Last saved ${new Date(payoutLastSavedAt).toLocaleString()}`
                      : 'Not saved yet.'}
                  </p>
                  <Button
                    onClick={handleSavePayoutMethod}
                    disabled={payoutSaving}
                    className="bg-[#004B8D] hover:bg-[#003a6d] text-white"
                    data-testid="payout-method-save-btn"
                  >
                    {payoutSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save payout details
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* P0 FIX: Contact Address Section - Required for listing creation */}
        <Card className="border-slate-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#004B8D]" />
              Contact Address
            </CardTitle>
            <CardDescription>Required for vehicle listings and rental agreements</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Street Address */}
            <div className="space-y-2">
              <Label htmlFor="address_line">Street Address *</Label>
              <Input 
                id="address_line" 
                name="address_line"
                value={formData.address_line}
                onChange={handleInputChange}
                placeholder="123 Main Street"
                data-testid="address-line-input"
              />
            </div>

            {/* City and State */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="city">City/Suburb *</Label>
                <Input 
                  id="city" 
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Sydney"
                  data-testid="city-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State *</Label>
                <select
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  data-testid="state-select"
                >
                  <option value="">Select state</option>
                  {australianStates.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Postcode and Country */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="postcode">Postcode *</Label>
                <Input 
                  id="postcode" 
                  name="postcode"
                  value={formData.postcode}
                  onChange={handleInputChange}
                  placeholder="2000"
                  maxLength={4}
                  data-testid="postcode-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">Country *</Label>
                <Input 
                  id="country" 
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  disabled
                  className="bg-slate-50"
                />
              </div>
            </div>

            <p className="text-xs text-slate-500 mt-2">
              * Required fields. Your address is kept private and only used for rental agreements.
            </p>
          </CardContent>
        </Card>

        {/* Operating Windows Section - P0 FIX: Host pickup/return availability */}
        <Card className="border-slate-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#004B8D]" />
              Operating Hours
            </CardTitle>
            <CardDescription>Set your availability for vehicle pickup and return</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Pickup Window */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Pickup Availability</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="pickup_start" className="text-xs text-slate-500">From</Label>
                  <select
                    id="pickup_start"
                    name="pickup_window_start"
                    value={formData.pickup_window_start || '08:00'}
                    onChange={handleInputChange}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="pickup-window-start"
                  >
                    {Array.from({length: 24}, (_, i) => `${i.toString().padStart(2, '0')}:00`).map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="pickup_end" className="text-xs text-slate-500">To</Label>
                  <select
                    id="pickup_end"
                    name="pickup_window_end"
                    value={formData.pickup_window_end || '18:00'}
                    onChange={handleInputChange}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="pickup-window-end"
                  >
                    {Array.from({length: 24}, (_, i) => `${i.toString().padStart(2, '0')}:00`).map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Return Window */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Return Availability</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="return_start" className="text-xs text-slate-500">From</Label>
                  <select
                    id="return_start"
                    name="return_window_start"
                    value={formData.return_window_start || '08:00'}
                    onChange={handleInputChange}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="return-window-start"
                  >
                    {Array.from({length: 24}, (_, i) => `${i.toString().padStart(2, '0')}:00`).map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="return_end" className="text-xs text-slate-500">To</Label>
                  <select
                    id="return_end"
                    name="return_window_end"
                    value={formData.return_window_end || '18:00'}
                    onChange={handleInputChange}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="return-window-end"
                  >
                    {Array.from({length: 24}, (_, i) => `${i.toString().padStart(2, '0')}:00`).map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <p className="text-xs text-slate-500 mt-2">
              These hours define when you're available for vehicle handovers. Renters will select times within these windows.
            </p>
          </CardContent>
        </Card>

        {/* Security Section */}
        <Card className="border-slate-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#004B8D]" />
              Security
            </CardTitle>
            <CardDescription>Manage your login and security settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Password */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Key className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-900">Password</p>
                  <p className="text-sm text-slate-500">Last changed: Never</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/forgot-password')}
              >
                Change
              </Button>
            </div>

            {/* Google Login Status */}
            {user?.google_id && (
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <div>
                    <p className="font-medium text-slate-900">Google Account</p>
                    <p className="text-sm text-slate-500">Connected</p>
                  </div>
                </div>
                <Badge className="bg-green-100 text-green-700 border-green-200">
                  Linked
                </Badge>
              </div>
            )}

            {/* Identity Verification */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-900">Identity Verification</p>
                  <p className="text-sm text-slate-500">
                    {user?.verification_status === 'verified' 
                      ? 'Your identity has been verified'
                      : 'Required for hosting'}
                  </p>
                </div>
              </div>
              {user?.verification_status !== 'verified' && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate('/verification')}
                >
                  Verify
                </Button>
              )}
              {user?.verification_status === 'verified' && (
                <Badge className="bg-green-100 text-green-700 border-green-200 gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Verified
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            className="bg-[#004B8D] hover:bg-[#003a6d] gap-2 min-w-[140px]"
            onClick={handleSaveProfile}
            disabled={saving}
            data-testid="save-profile-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </HostAppShell>
  );
};

export default HostAccountPage;
